const WINGET_ID = /^[A-Za-z][A-Za-z0-9+_-]*\.[A-Za-z][A-Za-z0-9.+_-]*$/
const WINGET_VERSION = /^\d+(\.\d+){1,}$/
const WINGET_SOURCE = /^(winget|msstore)$/i

const cleanWingetIdToken = (value) =>
  String(value || "")
    .trim()
    .replace(/-\d+(\.\d+){1,}$/, "")

export const wingetPackageId = (app) => {
  const raw = String(
    typeof app === "string"
      ? app
      : app?.id || app?.packageId || app?.PackageId || app?.appName || app?.name || app?.value || ""
  ).trim()
  if (!raw) return ""

  const tokens = raw.split(/\s+/).filter(Boolean)
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const token = cleanWingetIdToken(tokens[i])
    if (WINGET_SOURCE.test(token) || WINGET_VERSION.test(token)) continue
    if (WINGET_ID.test(token)) return token
  }

  const last = cleanWingetIdToken(tokens[tokens.length - 1])
  if (last && WINGET_ID.test(last)) return last
  if (
    tokens.length === 1 &&
    !WINGET_VERSION.test(tokens[0]) &&
    !WINGET_SOURCE.test(tokens[0])
  ) {
    return cleanWingetIdToken(tokens[0])
  }
  return ""
}

const uniqueIds = (values = []) => [
  ...new Set(
    (Array.isArray(values) ? values : [values])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  ),
]

export const collapseChocoPackageIds = (ids = []) => {
  const list = uniqueIds(ids)
  const byLower = new Map(list.map((id) => [id.toLowerCase(), id]))
  const drop = new Set()

  for (const id of list) {
    const installKey = `${id.replace(/\.install$/i, "")}.install`.toLowerCase()
    if (!/\.install$/i.test(id) && byLower.has(installKey)) drop.add(id)
  }

  const pythonIds = list.filter(
    (id) => /^python(\d+)?$/i.test(id) && !drop.has(id)
  )
  if (pythonIds.length > 1) {
    const versioned = pythonIds.filter((id) => /^python\d+$/i.test(id))
    const keep =
      versioned.sort().slice(-1)[0] ||
      pythonIds.find((id) => /^python3$/i.test(id)) ||
      pythonIds[0]
    pythonIds.filter((id) => id !== keep).forEach((id) => drop.add(id))
  }

  return list.filter((id) => !drop.has(id))
}

const chocoPackageId = (app) =>
  typeof app === "string"
    ? app
    : app?.id || app?.appName || app?.name || app?.value || app?.PackageId || ""

const isChocoFlag = (value) => String(value || "").trim().startsWith("-")

const CHOCO_STALE_CHECKSUM_RE =
  /^(googlechrome|firefox|firefoxesr|microsoft-edge|microsoftedge|opera|brave|chromium)(\.|$)/i

const CHOCO_STALE_KB_DEP_RE =
  /^(wireshark|winpcap|npcap|nmap)(\.|$)/i

export const chocoNeedsChecksumBypass = (id) => {
  const raw = String(id || "").trim()
  if (!raw || isChocoFlag(raw)) return false
  return CHOCO_STALE_CHECKSUM_RE.test(raw.replace(/\.install$/i, ""))
}

const chocoNeedsKbDepBypass = (id) => {
  const raw = String(id || "").trim()
  if (!raw || isChocoFlag(raw)) return false
  return CHOCO_STALE_KB_DEP_RE.test(raw.replace(/\.install$/i, ""))
}

const chocoIdsFromTokens = (tokens = []) =>
  (Array.isArray(tokens) ? tokens : String(tokens || "").split(","))
    .map((value) => String(value || "").trim())
    .filter((value) => value && !isChocoFlag(value))

export const formatChocoActionCommand = (action, id) => {
  const pkg = String(id || "").trim()
  if (!pkg) return null
  if (action !== "upgrade" && action !== "install") {
    return `choco-${action}:${pkg}`
  }
  const flags = ["-y"]
  if (chocoNeedsChecksumBypass(pkg)) {
    flags.push("--ignore-checksums", "--force")
  }
  if (chocoNeedsKbDepBypass(pkg)) {
    flags.push("--ignore-dependencies")
  }
  if (flags.length === 1) return `choco-${action}:${pkg}`
  flags.push("--execution-timeout", "1800", "--ignore-detected-reboot")
  return `custom-task:choco ${action} ${pkg} ${flags.join(" ")}`
}

export const chocoUpgradeCommands = (apps = []) => {
  const ids = collapseChocoPackageIds(
    (Array.isArray(apps) ? apps : [apps]).map(chocoPackageId)
  )
  const specials = ids.filter(
    (id) => chocoNeedsChecksumBypass(id) || chocoNeedsKbDepBypass(id)
  )
  const regular = ids.filter((id) => !specials.includes(id))
  const cmds = []
  if (regular.length === 1) {
    cmds.push(formatChocoActionCommand("upgrade", regular[0]))
  } else if (regular.length > 1) {
    cmds.push(
      `custom-task:choco upgrade ${regular.join(" ")} -y --execution-timeout 1800 --ignore-detected-reboot`
    )
  }
  for (const id of specials) {
    cmds.push(formatChocoActionCommand("upgrade", id))
  }
  return cmds.filter(Boolean)
}

export const wingetUpgradeCommands = (apps = []) => {
  const ids = [
    ...new Set(
      (Array.isArray(apps) ? apps : [apps]).map(wingetPackageId).filter(Boolean)
    ),
  ]
  return ids.map((id) => `winget-upgrade:${id}`)
}

export const wingetUpgradeCommand = (apps = []) =>
  wingetUpgradeCommands(apps)[0] || null

export const packageId = (app = {}) =>
  String(
    app.packageId || app.PackageId || app.id || app.Id || app.appName || app.name || ""
  ).trim()

export const inferSource = (app = {}) => {
  const explicit = String(app.source || "").toLowerCase()
  if (explicit === "winget" || explicit === "choco") return explicit
  const id = packageId(app)
  if (id && /[A-Z]/.test(id) && /^[A-Za-z0-9][A-Za-z0-9._+-]*\.[A-Za-z0-9._+-]+$/.test(id)) {
    return "winget"
  }
  return "choco"
}

export const installCommand = (app = {}) => {
  const source = inferSource(app)
  const id = source === "winget" ? wingetPackageId(app) : packageId(app)
  if (!id) return null
  return source === "winget"
    ? `winget-install:${id}`
    : formatChocoActionCommand("install", id)
}

const UNINSTALL_ARP_NAMES = {
  "microsoft.msodbcsql.17": "Microsoft ODBC Driver 17 for SQL Server",
  "microsoft.msodbcsql.18": "Microsoft ODBC Driver 18 for SQL Server",
  "anydesk.anydesk": "AnyDesk",
  "anydesk.install": "AnyDesk",
  "anydesk": "AnyDesk",
}

export const uninstallCommand = (app = {}) => {
  const source = inferSource(app)
  const id = source === "winget" ? wingetPackageId(app) : packageId(app)
  if (!id) return null
  const arp = UNINSTALL_ARP_NAMES[id.toLowerCase()]
  if (arp) return `app-uninstall:${arp}`
  return source === "winget" ? `winget-uninstall:${id}` : `choco-uninstall:${id}`
}
